// src/config/api.ts
// ImportMetaEnv is provided by Vite

import { getApiOrigin } from './platform';
import { loginPathWithNext } from './loginRedirect';
import { toast } from 'sonner';
import { buildHttpError, DEFAULT_REQUEST_TIMEOUT, isMarkup, readBody, toRequestError } from './apiError';

// Force production mode for deployed app
const isProduction = import.meta.env.PROD;

// Get the base URL from environment variables, with different defaults for dev/prod
let apiServerUrl: string;

// For local development, prioritize the environment variable
if (!isProduction) {
    // First try to use the environment variable
    if (import.meta.env.VITE_API_SERVER_URL) {
        apiServerUrl = import.meta.env.VITE_API_SERVER_URL.replace(/\/+$/, '');
    }
    // If running in Visual Studio's SPA proxy mode, use a hardcoded development URL
    else {
        // Use the server URL from launchSettings.json (HTTP instead of HTTPS to avoid SSL issues)
        apiServerUrl = 'http://localhost:5183';
    }
} else {
    // In production, use the current origin WITHOUT appending /api
    // This is critical to prevent double /api/api/ paths
    apiServerUrl = getApiOrigin();
    apiServerUrl = apiServerUrl.replace(/\/+$/, '');

    // Important: In production with VITE_API_SERVER_URL=/api, don't store it in SERVER_URL
    // The /api path will be added by createEndpoint instead
}

// Server URLs - single source of truth
// Make sure we respect the page's protocol to avoid mixed content issues
export const SERVER_URL = apiServerUrl;

// Helper to get the server URL with the correct protocol based on current page
export function getServerUrlWithMatchingProtocol(): string {
    if (typeof window !== 'undefined') {
        // If SERVER_URL is empty or just a path, use the current origin
        if (!SERVER_URL || SERVER_URL.startsWith('/')) {
            return getApiOrigin();
        }

        // Force production mode to use current origin for API requests
        if (import.meta.env.PROD) {
            return getApiOrigin();
        }

        const isLocalhost = SERVER_URL.includes('localhost') || SERVER_URL.includes('127.0.0.1');

        // For localhost development, don't enforce HTTPS as it likely uses HTTP without proper certs
        if (isLocalhost) {
            return SERVER_URL;
        }

        // For production with HTTPS
        if (import.meta.env.VITE_USE_HTTPS === 'true') {
            // Ensure HTTPS protocol
            const url = SERVER_URL.replace(/^http:\/\//i, 'https://');
            return url;
        }

        return SERVER_URL;
    }
    // If we get here, we're likely in a server-side rendering context
    // Return a safe default empty string
    return '';
}

// API path segments
const API_PATH = 'api';
const AUTH_PATH = 'auth';

// Helper to construct full URLs correctly
const createEndpoint = (path: string): string => {
    // Remove leading/trailing slashes for consistency
    const cleanPath = path.replace(/^\/+|\/+$/g, '');

    // Check if the API server URL already includes the API path
    const apiServerUrl = import.meta.env.VITE_API_SERVER_URL || '';
    const apiServerUrlIncludesApiPath = apiServerUrl.includes('/api');

    // In production mode, we need special handling
    if (import.meta.env.PROD) {
        // Get the base URL with protocol matching
        const baseUrl = getServerUrlWithMatchingProtocol();
        const baseUrlNoTrailingSlash = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

        // Construct the endpoint with /api path
        const endpoint = `${baseUrlNoTrailingSlash}/api/${cleanPath}`;

        return endpoint;
    }

    // For development or client-side router paths (relative paths)
    if (!SERVER_URL) {
        // If the API_PATH is already in the environment variable, don't add it again
        const endpoint = apiServerUrlIncludesApiPath
            ? `${apiServerUrl}/${cleanPath}`.replace('/api/api/', '/api/')
            : `/${API_PATH}/${cleanPath}`;
        return endpoint;
    }

    // For absolute URLs to the API server in development, respect the current protocol
    const baseUrl = getServerUrlWithMatchingProtocol();
    const baseUrlNoTrailingSlash = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

    // If the API_PATH is already in the environment variable, don't add it again
    let endpoint;
    if (baseUrl.includes('/api')) {
        endpoint = `${baseUrlNoTrailingSlash}/${cleanPath}`;
        // Fix double api path if it occurs
        endpoint = endpoint.replace('/api/api/', '/api/');
    } else {
        endpoint = `${baseUrlNoTrailingSlash}/${API_PATH}/${cleanPath}`;
    }

    // Final safety check to prevent double /api/api/ paths
    endpoint = endpoint.replace(/\/api\/api\//g, '/api/');

    return endpoint;
};

// Base API URL - always use relative path for API calls
export const API_BASE_URL = `/${API_PATH}`;

// Active business selection — used to tell the server which business a scoped request is for.
// Lives as a module variable (not React state) because fetchWithAuth is a plain module-level
// function with no context access. Seeded from localStorage so a page refresh doesn't lose it;
// AuthContext keeps it in sync as businesses load / the user switches.
const ACTIVE_BUSINESS_KEY = 'activeBusinessId';
let activeBusinessId: string | null =
    typeof localStorage !== 'undefined' ? localStorage.getItem(ACTIVE_BUSINESS_KEY) : null;

export function setActiveBusinessId(id: string | null): void {
    activeBusinessId = id;
    if (typeof localStorage !== 'undefined') {
        if (id) localStorage.setItem(ACTIVE_BUSINESS_KEY, id);
        else localStorage.removeItem(ACTIVE_BUSINESS_KEY);
    }
}

// Read-only accessor so AuthContext's 403 recovery can capture "what id did the rejected
// request actually send" at the moment of failure, instead of relying on a (possibly stale)
// React state closure.
export function getActiveBusinessId(): string | null {
    return activeBusinessId;
}

// Callback AuthContext registers so fetchWithAuth can report "the business id we sent was
// rejected" without importing React context into this module. Fired only when a request that
// actually carried X-Business-Id comes back with a genuine access-denied 403 — see fetchWithAuth
// below for exactly what qualifies.
type BusinessAccessDeniedHandler = () => void;
let businessAccessDeniedHandler: BusinessAccessDeniedHandler | null = null;

export function setBusinessAccessDeniedHandler(handler: BusinessAccessDeniedHandler | null): void {
    businessAccessDeniedHandler = handler;
}

// Common headers for all API requests
export const getDefaultHeaders = (): HeadersInit => {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    };
    if (activeBusinessId) headers['X-Business-Id'] = activeBusinessId;
    return headers;
};

// Auth endpoints with correct path construction
export const AUTH_ENDPOINTS = {
    LOGIN: createEndpoint(`${AUTH_PATH}/login`),
    REGISTER: createEndpoint(`${AUTH_PATH}/register`),
    PROFILE_IMAGE: createEndpoint('profile-image'),
    FORGOT_PASSWORD: createEndpoint(`${AUTH_PATH}/forgot-password`),
    VALIDATE_RESET_TOKEN: createEndpoint(`${AUTH_PATH}/validate-reset-token`),
    RESET_PASSWORD: createEndpoint(`${AUTH_PATH}/reset-password`),
    LOGOUT: createEndpoint(`${AUTH_PATH}/logout`),
    USER: createEndpoint(`${AUTH_PATH}/user`),
    UPLOAD_PROFILE_PICTURE: createEndpoint(`${AUTH_PATH}/upload-profile-picture`),
    CLEAR_PROFILE_PICTURE: createEndpoint(`${AUTH_PATH}/clear-profile-picture`),
    UPDATE_USER_INFO: createEndpoint(`${AUTH_PATH}/update-user-info`),
    CHANGE_PASSWORD: createEndpoint(`${AUTH_PATH}/change-password`),
    CONFIRM_EMAIL: createEndpoint(`${AUTH_PATH}/confirm-email`),
    REFRESH_TOKEN: createEndpoint(`${AUTH_PATH}/refresh-token`),
};

// Business endpoints
export const BUSINESS_ENDPOINTS = {
    ME: createEndpoint('Business/me'),
    MY_BUSINESSES: createEndpoint('Business/my-businesses'),
    SWITCH: createEndpoint('Business/switch'),
    UPDATE: (id: string) => createEndpoint(`Business/${id}`),
    UPDATE_ME: createEndpoint('Business/me'),
    LOGO: createEndpoint('BusinessLogo'),
    UPLOAD_LOGO: createEndpoint('BusinessLogo/upload'),
    INIT: createEndpoint('Business/me/init'),
    DISMISS_SETUP_GUIDE: createEndpoint('Business/me/dismiss-setup-guide'),
    EMAIL_SAMPLE: createEndpoint('ExclusiveClients/email-sample'),
};

// Category taxonomy endpoint
export const CATEGORY_ENDPOINTS = {
    LIST: createEndpoint('Categories'),
};

// Helper to get business logo URL (current user's business)
export function getBusinessLogoUrl(): string {
    if (import.meta.env.PROD) {
        const origin = getApiOrigin();
        const timestamp = new Date().getTime();
        return `${origin}/api/BusinessLogo?v=${timestamp}`;
    }
    const base = BUSINESS_ENDPOINTS.LOGO;
    const ts = new Date().getTime();
    return `${base}?v=${ts}`;
}

// Helper to get the PUBLIC logo URL for an arbitrary business (marketplace cards/profiles).
// Distinct from getBusinessLogoUrl() above, which is unparameterised and always resolves to the
// current authenticated user's own business via /api/BusinessLogo — that function is depended on
// elsewhere and must not be overloaded. This one hits the anonymous
// /api/public/business-logo/{businessId} route (PublicBusinessLogoController) instead.
export function getPublicBusinessLogoUrl(businessId: string): string {
    const origin = import.meta.env.PROD ? getApiOrigin() : (SERVER_URL || getApiOrigin());
    return `${origin}/api/public/business-logo/${encodeURIComponent(businessId)}`;
}

// Business data returned by the API
export interface BusinessData {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    description?: string;
    website?: string;
    backgroundColor?: string;
    mainContactName?: string;
    shortHeading?: string;
    about?: string;
    socialLinks?: {
        instagram?: string;
        facebook?: string;
        youtube?: string;
        linkedin?: string;
        x?: string;
        tiktok?: string;
    };
    councilAreas?: string[];
    /**
     * This business is on the all-councils model: entitled to every council, removing the ones it
     * does not want. Absent/false means a legacy explicit `councilAreas` list.
     */
    allCouncilAreas?: boolean;
    /** The councils removed from the all-councils model. Meaningless while `allCouncilAreas` is false. */
    excludedCouncilAreas?: string[];
    keywords?: string[];
    categories?: string[];
    industries?: string[];
    businessTypes?: string[];
    subscriptionStatus?: string;
    subscriptionId?: string;
    planId?: string;
    planName?: string;
    subscriptionEndDate?: string;
    cancelAtPeriodEnd?: boolean;
    cancelAt?: string;
    isSandbox?: boolean;
    setupGuideDismissed?: boolean;
    exclusiveEmailsEnabled?: boolean;
    marketplaceListingEnabled?: boolean;
    preferredDaCategories?: string[];
    preferredPropertyTypes?: string[];
    emailIntegrationEnabled?: boolean;
    [key: string]: unknown;
}

// Business API
export const businessApi = {
    getMyBusiness: async (): Promise<BusinessData> => {
        return fetchWithAuth<BusinessData>(BUSINESS_ENDPOINTS.ME, {
            method: 'GET',
            headers: getDefaultHeaders(),
        });
    },

    /**
     * The current user's business, or null when they don't have one yet.
     *
     * Deliberately does NOT create one. This used to POST /me/init on a 404, which
     * silently minted a 14-day trial with no council areas and no T&C acceptance for
     * anyone who abandoned sign-up before the council step — and a trial with no
     * councils produces no leads. Callers must send a user with no business back to
     * sign-up to pick councils; only that flow may create a business.
     */
    getMyBusinessOrNull: async (): Promise<BusinessData | null> => {
        try {
            return await businessApi.getMyBusiness();
        } catch (e: unknown) {
            const status = (e as { response?: { status?: number } })?.response?.status;
            if (status === 404) return null;
            throw e;
        }
    },

    initMyBusiness: async (): Promise<BusinessData> => {
        return fetchWithAuth<BusinessData>(BUSINESS_ENDPOINTS.INIT, {
            method: 'POST',
            headers: getDefaultHeaders(),
        });
    },

    updateBusiness: async (id: string, data: Partial<Record<string, unknown>>): Promise<BusinessData> => {
        return fetchWithAuth<BusinessData>(BUSINESS_ENDPOINTS.UPDATE(id), {
            method: 'PUT',
            headers: getDefaultHeaders(),
            body: JSON.stringify(data),
        });
    },

    updateMyBusiness: async (data: Partial<Record<string, unknown>>): Promise<BusinessData> => {
        return fetchWithAuth<BusinessData>(BUSINESS_ENDPOINTS.UPDATE_ME, {
            method: 'PUT',
            headers: getDefaultHeaders(),
            body: JSON.stringify(data),
        });
    },

    emailSampleExclusiveListing: async (): Promise<{ message: string; to: string }> => {
        return fetchWithAuth<{ message: string; to: string }>(BUSINESS_ENDPOINTS.EMAIL_SAMPLE, {
            method: 'POST',
            headers: getDefaultHeaders(),
        });
    },

    /** Clears the business's logo. Removing one that is already gone is a success, not an error. */
    deleteBusinessLogo: async (): Promise<void> => {
        await fetchWithAuth<void>(BUSINESS_ENDPOINTS.LOGO, { method: 'DELETE', headers: getDefaultHeaders() });
    },

    uploadBusinessLogo: async (file: File): Promise<{ logoUrl: string }> => {
        const formData = new FormData();
        formData.append('file', file);
        // fetchWithAuth: the shared ApiError (the server's message for a refused file, friendly
        // copy for a proxy's 413 page), the session-expiry redirect, and X-Business-Id.
        return fetchWithAuth<{ logoUrl: string }>(BUSINESS_ENDPOINTS.UPLOAD_LOGO, {
            method: 'POST',
            body: formData,
        });
    },

    dismissSetupGuide: async (): Promise<void> => {
        await fetchWithAuth<void>(BUSINESS_ENDPOINTS.DISMISS_SETUP_GUIDE, {
            method: 'POST',
            headers: getDefaultHeaders(),
        });
    },

    getMyBusinesses: async (): Promise<{ id: string; name: string; subscriptionStatus?: string; subscriptionEndDate?: string; isOwner: boolean; isSandbox?: boolean; planName?: string }[]> => {
        return fetchWithAuth(BUSINESS_ENDPOINTS.MY_BUSINESSES, {
            method: 'GET',
            headers: getDefaultHeaders(),
        });
    },

    switchBusiness: async (businessId: string): Promise<Record<string, unknown>> => {
        return fetchWithAuth(BUSINESS_ENDPOINTS.SWITCH, {
            method: 'POST',
            headers: getDefaultHeaders(),
            body: JSON.stringify({ businessId }),
        });
    },
};

// Category taxonomy API
export const categoriesApi = {
    getCategories: async (): Promise<string[]> => {
        return fetchWithAuth<string[]>(CATEGORY_ENDPOINTS.LIST, {
            method: 'GET',
            headers: getDefaultHeaders(),
        });
    },
};

export const INTEGRATION_ENDPOINTS = {
    STATUS: createEndpoint('integrations/email/status'),
    CONNECT: (provider: string) => createEndpoint(`integrations/email/${provider}/connect`),
    DISCONNECT: createEndpoint('integrations/email/disconnect'),
    TEST: createEndpoint('integrations/email/test'),
};

export interface EmailIntegrationStatus {
    connected: boolean;
    provider?: string | null;
    connectedEmail?: string | null;
    status: string;
}

export const integrationsApi = {
    getStatus: async (): Promise<EmailIntegrationStatus> =>
        fetchWithAuth<EmailIntegrationStatus>(INTEGRATION_ENDPOINTS.STATUS, {
            method: 'GET', headers: getDefaultHeaders(),
        }),
    connectUrl: async (provider: 'google' | 'microsoft'): Promise<{ url: string }> =>
        fetchWithAuth<{ url: string }>(INTEGRATION_ENDPOINTS.CONNECT(provider), {
            method: 'GET', headers: getDefaultHeaders(),
        }),
    disconnect: async (): Promise<{ message: string }> =>
        fetchWithAuth<{ message: string }>(INTEGRATION_ENDPOINTS.DISCONNECT, {
            method: 'POST', headers: getDefaultHeaders(),
        }),
    sendTest: async (): Promise<{ message: string }> =>
        fetchWithAuth<{ message: string }>(INTEGRATION_ENDPOINTS.TEST, {
            method: 'POST', headers: getDefaultHeaders(),
        }),
};

export const MARKETING_ENDPOINTS = {
    LIST: createEndpoint('marketing-emails'),
    ITEM: (id: string) => createEndpoint(`marketing-emails/${id}`),
    TEST: (id: string) => createEndpoint(`marketing-emails/${id}/test`),
    IMAGES: createEndpoint('marketing-emails/images'),
    TEMPLATES: createEndpoint('marketing-emails/templates'),
    TEMPLATE: (key: string) => createEndpoint(`marketing-emails/templates/${key}`),
    RECIPIENTS: createEndpoint('marketing-emails/recipients'),
    VALIDATE_RECIPIENT: createEndpoint('marketing-emails/recipients/validate'),
    EXCLUDE_RECIPIENT: createEndpoint('marketing-emails/recipients/exclude'),
    SEND: (id: string) => createEndpoint(`marketing-emails/${id}/send`),
    SENDS: (id: string) => createEndpoint(`marketing-emails/${id}/sends`),
};

export interface MarketingEmailSummary { id: string; name: string; subject?: string; updatedAt: string; }
export interface MarketingEmailDetail extends MarketingEmailSummary { designJson: string; html: string; }

/** A contact this business's exclusive listings have already reached. */
export interface MarketingRecipient {
    email: string;
    name: string;
    /** How many DAs this contact is linked to — one contact spans many applications. */
    daCount: number;
    lastDispatchedAt: string;
}

export interface MarketingRecipientList {
    recipients: MarketingRecipient[];
    /**
     * Contacts withheld because they're globally unsubscribed or excluded by this business.
     * Surfaced so the UI can say the list is short on purpose rather than looking wrong.
     */
    suppressedCount: number;
}

export type RecipientRejection = 'InvalidFormat' | 'GloballyUnsubscribed' | 'ExcludedByBusiness';

export interface RecipientValidation {
    ok: boolean;
    reason?: RecipientRejection | null;
    message?: string | null;
}

export interface MarketingSendResult {
    sendId: string;
    queued: number;
    rejected: { email: string; reason: RecipientRejection }[];
}

export interface MarketingSendSummary {
    sendId: string;
    queuedAt: string;
    total: number;
    sent: number;
    failed: number;
    suppressed: number;
    queued: number;
    recipients: { email: string; status: string; sentAt?: string | null; error?: string | null }[];
}

export const marketingApi = {
    list: async (): Promise<MarketingEmailSummary[]> =>
        fetchWithAuth<MarketingEmailSummary[]>(MARKETING_ENDPOINTS.LIST, { method: 'GET', headers: getDefaultHeaders() }),
    get: async (id: string): Promise<MarketingEmailDetail> =>
        fetchWithAuth<MarketingEmailDetail>(MARKETING_ENDPOINTS.ITEM(id), { method: 'GET', headers: getDefaultHeaders() }),
    create: async (name: string): Promise<{ id: string }> =>
        fetchWithAuth<{ id: string }>(MARKETING_ENDPOINTS.LIST, { method: 'POST', headers: getDefaultHeaders(), body: JSON.stringify({ name }) }),
    update: async (id: string, data: { name?: string; subject?: string; designJson?: string; html?: string }): Promise<{ id: string }> =>
        fetchWithAuth<{ id: string }>(MARKETING_ENDPOINTS.ITEM(id), { method: 'PUT', headers: getDefaultHeaders(), body: JSON.stringify(data) }),
    remove: async (id: string): Promise<{ message: string }> =>
        fetchWithAuth<{ message: string }>(MARKETING_ENDPOINTS.ITEM(id), { method: 'DELETE', headers: getDefaultHeaders() }),
    sendTest: async (id: string): Promise<{ message: string }> =>
        fetchWithAuth<{ message: string }>(MARKETING_ENDPOINTS.TEST(id), { method: 'POST', headers: getDefaultHeaders() }),
    uploadImageUrl: (): string => MARKETING_ENDPOINTS.IMAGES,
    listTemplates: async (): Promise<{ key: string; name: string }[]> =>
        fetchWithAuth<{ key: string; name: string }[]>(MARKETING_ENDPOINTS.TEMPLATES, {
            method: 'GET', headers: getDefaultHeaders(),
        }),
    getTemplate: async (key: string): Promise<{ html: string }> =>
        fetchWithAuth<{ html: string }>(MARKETING_ENDPOINTS.TEMPLATE(key), {
            method: 'GET', headers: getDefaultHeaders(),
        }),
    recipients: async (): Promise<MarketingRecipientList> =>
        fetchWithAuth<MarketingRecipientList>(MARKETING_ENDPOINTS.RECIPIENTS, {
            method: 'GET', headers: getDefaultHeaders(),
        }),
    validateRecipient: async (email: string): Promise<RecipientValidation> =>
        fetchWithAuth<RecipientValidation>(MARKETING_ENDPOINTS.VALIDATE_RECIPIENT, {
            method: 'POST', headers: getDefaultHeaders(), body: JSON.stringify({ email }),
        }),
    excludeRecipient: async (email: string, reason?: string): Promise<{ message: string }> =>
        fetchWithAuth<{ message: string }>(MARKETING_ENDPOINTS.EXCLUDE_RECIPIENT, {
            method: 'POST', headers: getDefaultHeaders(), body: JSON.stringify({ email, reason }),
        }),
    send: async (id: string, emails: string[]): Promise<MarketingSendResult> =>
        fetchWithAuth<MarketingSendResult>(MARKETING_ENDPOINTS.SEND(id), {
            method: 'POST', headers: getDefaultHeaders(), body: JSON.stringify({ emails }),
        }),
    sends: async (id: string): Promise<{ sends: MarketingSendSummary[] }> =>
        fetchWithAuth<{ sends: MarketingSendSummary[] }>(MARKETING_ENDPOINTS.SENDS(id), {
            method: 'GET', headers: getDefaultHeaders(),
        }),
};

// Function to get the full API URL
export const getFullApiUrl = (endpoint: string): string => {
    const path = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    return createEndpoint(path);
};

// Notification endpoints
export const NOTIFICATION_ENDPOINTS = {
    NOTIFICATIONS: createEndpoint('UserNotifications'),
    NOTIFICATION_COUNT: createEndpoint('UserNotifications/count'),
    NOTIFICATION_READ: (id: string) => createEndpoint(`UserNotifications/${id}/mark-read`),
    MARK_ALL_READ: createEndpoint('UserNotifications/read-all'),
    NOTIFICATION_PREFERENCES: createEndpoint('NotificationPreferences'),
    NOTIFICATION_PREFERENCE_CHECK: (id: string) => createEndpoint(`NotificationPreferences/${id}/check`),
};

// Search and Address endpoints
export const SEARCH_ENDPOINTS = {
    SEARCH: createEndpoint('address/search'),
    ADDRESS: createEndpoint('address'),
    GET_SEARCH_HISTORY: createEndpoint('SearchHistory'),
    DELETE_SEARCH_HISTORY: (id: string) => createEndpoint(`SearchHistory/${id}`),
    CLEAR_SEARCH_HISTORY: createEndpoint('SearchHistory/clear'),
    // Data Extraction endpoints for Gold Coast City
    GOLD_COAST_SCRAPING: createEndpoint('GoldCoastCityScraper'),
    // Data Extraction endpoints for South Australia
    SOUTH_AUSTRALIA_SCRAPING: createEndpoint('SouthAustraliaScraper'),
    // Get list of supported councils (hardcoded in client now)
    COUNCIL_SCRAPING_COUNCILS: [],
    // Legacy endpoint kept for compatibility
    COUNCIL_SCRAPING: createEndpoint('CouncilScraping'),
    // Every scraped council (not only subscribable ones) plus South Australia — drives the region dropdown.
    COUNCILS_REGIONS: createEndpoint('councils/regions'),
};

// Enhanced fetch wrapper. Every non-2xx response throws ONE well-formed ApiError (see
// config/apiError.ts): a readable `message` (the server's own when it sent one, otherwise a
// status-based fallback), plus `status` and `response: { status, data }` for callers that
// branch on the body.

// Extended fetch options with custom timeout and retry count
type FetchWithAuthOptions = RequestInit & {
    /** Custom timeout in milliseconds */
    timeout?: number;
    /** @internal */
    _retryCount?: number;
};

// Default timeout values (in milliseconds)
const DEFAULT_TIMEOUT = DEFAULT_REQUEST_TIMEOUT; // 30 seconds
const SEARCH_TIMEOUT = 300000; // 5 minutes
const EXTRACTION_TIMEOUT = 10800000; // 180 minutes for data extraction operations

export const fetchWithAuth = async <T = unknown>(
    url: string,
    options: FetchWithAuthOptions = {}
): Promise<T> => {
    // Extract and remove internal options before passing to fetch
    const { _retryCount, timeout, ...fetchOptions } = options;
    const controller = new AbortController();

    // Use appropriate timeout based on endpoint type
    let requestTimeout = timeout || DEFAULT_TIMEOUT;

    // Use longer timeout for search endpoints
    if (url.includes('address/search')) {
        requestTimeout = SEARCH_TIMEOUT;
    }
    // Use even longer timeout for data extraction endpoints
    else if (url.includes('GoldCoastCityScraper') || url.includes('SouthAustraliaScraper')) {
        requestTimeout = EXTRACTION_TIMEOUT;
    }

    const timeoutId = setTimeout(() => controller.abort(), requestTimeout);

    // Whether this specific request carried a business id — a necessary precondition for a 403
    // to mean "the stored business selection is stale" (see the 403 handling below).
    const sentBusinessId = Boolean(activeBusinessId);

    let response: Response;
    let body: { data: unknown; text: string; parsed: boolean };
    try {
        // Get the full URL
        const fullUrl = url.startsWith('http') ? url : `${getServerUrlWithMatchingProtocol()}/${url.replace(/^\/+/, '')}`;

        // Prepare headers
        const headers = new Headers({
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            ...(options.headers || {})
        });
        // Attach unconditionally here (not just via getDefaultHeaders()) so every call routed
        // through fetchWithAuth carries it, even callers that pass their own headers object.
        if (activeBusinessId) headers.set('X-Business-Id', activeBusinessId);
        // A FormData body (file upload) needs the browser's multipart Content-Type with its
        // boundary — the JSON default would make the server unable to read the file.
        if (typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData) headers.delete('Content-Type');

        response = await fetch(fullUrl, {
            ...fetchOptions,
            headers,
            credentials: 'include',
            mode: 'cors',
            signal: controller.signal
        });
        // Read inside the timeout too: a server that sends headers and then stalls the body
        // must not hang the caller forever.
        body = await readBody(response);
    } catch (error) {
        // Timeout (AbortError) and network failures (TypeError — "Failed to fetch", Safari's
        // "Load failed", Firefox's "NetworkError…") become distinct friendly messages.
        throw toRequestError(error);
    } finally {
        clearTimeout(timeoutId);
    }

    // A 2xx HTML page is the SPA fallback answering a misrouted /api call — not success.
    if (response.ok && isMarkup(body.text)) throw buildHttpError(response, body);

    if (response.ok) {
        if (body.parsed) return body.data as T;
        // A successful non-JSON (or empty) response — kept as before for existing callers.
        return {
            success: true,
            message: 'Success',
            data: body.text
        } as unknown as T;
    }

    const error = buildHttpError(response, body);

    if (response.status === 403 && (body.data as { error?: unknown } | undefined)?.error === 'read_only_impersonation') {
        toast.error('Read-only session — change not saved.');
    }

    // A 403 on a request that carried X-Business-Id, with NO parseable response body, is the
    // server's bare Forbid() from ResolveBusinessAsync — i.e. the business id we sent was
    // genuinely rejected (stale localStorage selection: removed membership, deleted
    // business). Any 403 with a JSON body (read_only_impersonation, subscription-not-active,
    // other feature-gate Forbid()s) is a different, unrelated permission check that happens
    // to also carry X-Business-Id — leave those alone. Let AuthContext recover by clearing
    // the stale id and re-resolving; this request itself still fails/throws either way.
    if (sentBusinessId && response.status === 403 && !body.parsed && businessAccessDeniedHandler) {
        businessAccessDeniedHandler();
    }

    // Session expired: send the user to sign in, carrying the page they were on as `next` —
    // SignInForm returns there once they have signed in again.
    if (response.status === 401) {
        window.location.href = loginPathWithNext(window.location.pathname, window.location.search);
    }

    throw error;
};

// Import types
import {
    Notification,
    NotificationPreference,
    ProcessingStatus,
    SearchResponse,
    SearchHistoryItem,
} from '../types/api';
import type {
    DaDetailResponse,
    DaMatchesQuery,
    DaMatchesResponse,
    DaMatchesSummary,
} from '../types/da';

// Update the SearchHistoryResponse type to match the backend response
export type SearchHistoryResponse = SearchHistoryItem[];

// ... (rest of the code remains the same)
// Search API functions
export const searchApi = {
    // Search addresses
    searchAddresses: async (params: {
        query: string;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
        limit?: number;
        skip?: number;
        council?: string;
        startDate?: string;
        endDate?: string;
        withContacts?: boolean;
    }): Promise<SearchResponse> => {
        // Build query parameters
        const queryParams = new URLSearchParams();

        // Add query parameters
        if (params.query) queryParams.append('query', params.query);
        if (params.sortBy) queryParams.append('sortBy', params.sortBy);
        if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
        if (params.limit) queryParams.append('limit', params.limit.toString());
        if (params.skip) queryParams.append('skip', params.skip.toString());
        if (params.council) queryParams.append('council', params.council);
        if (params.startDate) queryParams.append('startDate', params.startDate);
        if (params.endDate) queryParams.append('endDate', params.endDate);
        if (params.withContacts) queryParams.append('withContacts', 'true');

        const queryString = queryParams.toString();
        const endpoint = SEARCH_ENDPOINTS.SEARCH + (queryString ? `?${queryString}` : '');

        // Make the API call with proper typing
        const responseData = await fetchWithAuth<SearchResponse>(endpoint, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        });

        if (!responseData) {
            throw new Error('No response data received');
        }

        // Check for error in response data
        if (responseData.error) {
            throw new Error(responseData.error);
        }

        return responseData;
    },

    // Save search history (kept for backward compatibility)
    saveSearchHistory: async (data: {
        searchQuery: string;
        resultCount: number;
        startDate?: string;
        endDate?: string;
    }): Promise<SearchHistoryItem> => {
        return searchHistoryApi.saveSearchHistory({
            searchQuery: data.searchQuery,
            resultCount: data.resultCount,
            searchParams: {
                startDate: data.startDate,
                endDate: data.endDate
            }
        });
    },

};

// Search History API functions
export const searchHistoryApi = {
    // Get search history with pagination
    getSearchHistory: async (params: {
        limit?: number;
        skip?: number;
    } = {}): Promise<SearchHistoryResponse> => {
        const queryParams = new URLSearchParams();
        if (params.limit) queryParams.set('limit', params.limit.toString());
        if (params.skip) queryParams.set('skip', params.skip.toString());

        const queryString = queryParams.toString();
        const url = queryString
            ? `${SEARCH_ENDPOINTS.GET_SEARCH_HISTORY}?${queryString}`
            : SEARCH_ENDPOINTS.GET_SEARCH_HISTORY;

        // The backend returns the array directly, not wrapped in a response object
        return fetchWithAuth<SearchHistoryResponse>(url);
    },

    // Save a search to history
    saveSearchHistory: async (data: {
        searchQuery: string;
        resultCount: number;
        searchParams?: Record<string, unknown>;
    }): Promise<SearchHistoryItem> => {
        return fetchWithAuth<SearchHistoryItem>(SEARCH_ENDPOINTS.GET_SEARCH_HISTORY, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    // Delete a search history item
    deleteSearchHistoryItem: async (id: string): Promise<void> => {
        await fetchWithAuth(SEARCH_ENDPOINTS.DELETE_SEARCH_HISTORY(id), {
            method: 'DELETE',
        });
    },

    // Clear all search history
    clearSearchHistory: async (): Promise<void> => {
        await fetchWithAuth(SEARCH_ENDPOINTS.CLEAR_SEARCH_HISTORY, {
            method: 'DELETE',
            headers: getDefaultHeaders()
        });
    },
};

// Every council key the client can encounter: South Australia plus whatever CouncilRegistry.Enabled
// scrapes (Gold Coast, Brisbane, and future QLD councils). Kept as a plain string — CouncilRegistry
// is config-driven, so a union of literal council names would make new councils untypeable.
export type CouncilKey = string;

// Reference data describing which councils exist — used to drive the region dropdown so a newly
// scraped council (e.g. Brisbane) shows up without a client code change.
export const councilsApi = {
    getRegions: async (): Promise<{ key: string; displayName: string }[]> =>
        fetchWithAuth(SEARCH_ENDPOINTS.COUNCILS_REGIONS, {
            method: 'GET',
            headers: getDefaultHeaders(),
        }),
};

/** A named set of Development Map filters, kept against the business. */
export interface SavedMapView {
    id: string;
    name: string;
    state: string;
    /** Null means every council area in the state. */
    councilArea: string | null;
    categories: string[];
    /** '30d' | '90d' | '12m' | 'custom'. A preset re-resolves on load; only custom pins dates. */
    datePreset: string;
    from: string | null;
    to: string | null;
    createdAt: string;
}

export interface SaveMapViewRequest {
    name: string;
    state: string;
    councilArea?: string | null;
    categories?: string[];
    datePreset: string;
    from?: string | null;
    to?: string | null;
}

export const savedMapViewsApi = {
    list: async (): Promise<SavedMapView[]> =>
        fetchWithAuth(`${API_BASE_URL}/saved-map-views`, {
            method: 'GET',
            headers: getDefaultHeaders(),
        }),

    /** Saving under a name that already exists replaces that view rather than adding another. */
    save: async (view: SaveMapViewRequest): Promise<SavedMapView> =>
        fetchWithAuth(`${API_BASE_URL}/saved-map-views`, {
            method: 'POST',
            headers: getDefaultHeaders(),
            body: JSON.stringify(view),
        }),

    remove: async (id: string): Promise<void> => {
        await fetchWithAuth<void>(`${API_BASE_URL}/saved-map-views/${encodeURIComponent(id)}`, {
            method: 'DELETE',
            headers: getDefaultHeaders(),
        });
    },
};

export interface MatchAlertSettings {
    enabled: boolean;
    frequency: 'immediate' | 'daily' | 'weekly';
    /** The address that would actually be used: the override, or the contact email. */
    email: string;
    usingContactEmail: boolean;
    contactEmail: string;
    smsEnabled: boolean;
    /** The number that would actually be used: the override, or the business phone. */
    smsNumber: string;
    usingBusinessPhone: boolean;
    businessPhone: string;
    /** False when the environment has no ClickSend credentials, so the UI can say why. */
    smsAvailable: boolean;
    /** Council areas the business watches — what actually triggers a match. */
    councils: string[];
    /** DA categories the business watches. Empty means every category matches. */
    categories: string[];
    /** Composed server-side by the real alert composer, so the preview cannot drift from what sends. */
    smsPreview: string;
}

export const matchAlertsApi = {
    get: async (): Promise<MatchAlertSettings> =>
        fetchWithAuth(`${API_BASE_URL}/Business/me/alert-settings`, {
            method: 'GET',
            headers: getDefaultHeaders(),
        }),

    update: async (settings: {
        enabled: boolean;
        frequency: string;
        email?: string;
        smsEnabled: boolean;
        smsNumber?: string;
    }): Promise<MatchAlertSettings> =>
        fetchWithAuth(`${API_BASE_URL}/Business/me/alert-settings`, {
            method: 'PUT',
            headers: getDefaultHeaders(),
            body: JSON.stringify(settings),
        }),

    /** Sends one real, billable SMS to the saved number. Rate-limited per business by the server. */
    sendTestSms: async (): Promise<{ message: string }> =>
        fetchWithAuth(`${API_BASE_URL}/Business/me/alert-settings/test-sms`, {
            method: 'POST',
            headers: getDefaultHeaders(),
        }),
};

export const DA_ENDPOINTS = {
    MATCHES: `${API_BASE_URL}/development-applications/matches`,
    MATCHES_SUMMARY: `${API_BASE_URL}/development-applications/matches/summary`,
    MATCHES_CSV: `${API_BASE_URL}/development-applications/matches.csv`,
    SAVED: `${API_BASE_URL}/development-applications/saved`,
    SAVED_NEW_COUNT: `${API_BASE_URL}/development-applications/saved/new-count`,
    SAVED_VIEWED: `${API_BASE_URL}/development-applications/saved/viewed`,
    DETAIL: (councilKey: string, id: string) =>
        `${API_BASE_URL}/development-applications/${encodeURIComponent(councilKey)}/${encodeURIComponent(id)}`,
    SAVE: (councilKey: string, id: string) =>
        `${API_BASE_URL}/development-applications/${encodeURIComponent(councilKey)}/${encodeURIComponent(id)}/save`,
};

/**
 * Councils repeat as separate `councils=` entries rather than a comma-joined value: council keys
 * are free-form registry slugs and a joined string would break the first time one contained a comma.
 */
function buildMatchesQuery(query: DaMatchesQuery): string {
    const params = new URLSearchParams();
    (query.councils ?? []).forEach((c) => params.append('councils', c));
    // Repeated entries, like `councils` — the server binds these as string[].
    (query.propertyTypes ?? []).forEach((t) => params.append('propertyTypes', t));
    (query.categories ?? []).forEach((c) => params.append('categories', c));
    if (query.from) params.set('from', query.from);
    if (query.to) params.set('to', query.to);
    if (query.page) params.set('page', String(query.page));
    if (query.pageSize) params.set('pageSize', String(query.pageSize));
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}

// Development Applications API functions
export const developmentApplicationsApi = {
    /** Screen 1d. Returns only DAs this business matches, contact-free. */
    getMatches: async (query: DaMatchesQuery): Promise<DaMatchesResponse> =>
        fetchWithAuth<DaMatchesResponse>(`${DA_ENDPOINTS.MATCHES}${buildMatchesQuery(query)}`, {
            method: 'GET',
            headers: getDefaultHeaders(),
        }),

    /** Takes the same filters as getMatches — the tiles describe the same view as the table. */
    getMatchesSummary: async (query: Omit<DaMatchesQuery, 'page' | 'pageSize'>): Promise<DaMatchesSummary> =>
        fetchWithAuth<DaMatchesSummary>(`${DA_ENDPOINTS.MATCHES_SUMMARY}${buildMatchesQuery(query)}`, {
            method: 'GET',
            headers: getDefaultHeaders(),
        }),

    /**
     * The whole filtered set as CSV, built server-side — not the 25 rows the table happens to be
     * showing. Deliberately NOT routed through fetchWithAuth: that helper parses every response as
     * JSON and would throw on a text/csv body before we ever saw it.
     *
     * `truncated` comes from the X-Matches-Truncated header rather than the body, because the body
     * is the file the user is about to open: the caveat has to reach the screen, not the download.
     */
    getMatchesCsv: async (
        query: Omit<DaMatchesQuery, 'page' | 'pageSize'>,
    ): Promise<{ blob: Blob; filename: string; truncated: boolean }> => {
        const path = `${DA_ENDPOINTS.MATCHES_CSV}${buildMatchesQuery(query)}`;
        const fullUrl = path.startsWith('http')
            ? path
            : `${getServerUrlWithMatchingProtocol()}/${path.replace(/^\/+/, '')}`;

        const headers = new Headers({ 'Accept': 'text/csv', 'X-Requested-With': 'XMLHttpRequest' });
        const businessId = getActiveBusinessId();
        if (businessId) headers.set('X-Business-Id', businessId);

        const response = await fetch(fullUrl, {
            method: 'GET',
            headers,
            credentials: 'include',
            mode: 'cors',
        });
        if (!response.ok) throw new Error(`Could not export matches (HTTP ${response.status})`);

        const disposition = response.headers.get('Content-Disposition') ?? '';
        const named = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);

        return {
            blob: await response.blob(),
            filename: named
                ? decodeURIComponent(named[1].trim())
                : `da-matches-${new Date().toISOString().slice(0, 10)}.csv`,
            truncated: (response.headers.get('X-Matches-Truncated') ?? '').trim().toLowerCase() === 'true',
        };
    },

    /** Screen 1e. */
    getDetail: async (councilKey: string, id: string): Promise<DaDetailResponse> =>
        fetchWithAuth<DaDetailResponse>(DA_ENDPOINTS.DETAIL(councilKey, id), {
            method: 'GET',
            headers: getDefaultHeaders(),
        }),

    save: async (councilKey: string, id: string): Promise<void> => {
        await fetchWithAuth<void>(DA_ENDPOINTS.SAVE(councilKey, id), {
            method: 'POST',
            headers: getDefaultHeaders(),
        });
    },

    unsave: async (councilKey: string, id: string): Promise<void> => {
        await fetchWithAuth<void>(DA_ENDPOINTS.SAVE(councilKey, id), {
            method: 'DELETE',
            headers: getDefaultHeaders(),
        });
    },

    getSaved: async (): Promise<DaMatchesResponse> =>
        fetchWithAuth<DaMatchesResponse>(DA_ENDPOINTS.SAVED, {
            method: 'GET',
            headers: getDefaultHeaders(),
        }),

    /** Saves this user has not seen on the Saved page yet — the sidebar badge. */
    getSavedNewCount: async (): Promise<{ count: number }> =>
        fetchWithAuth<{ count: number }>(DA_ENDPOINTS.SAVED_NEW_COUNT, {
            method: 'GET',
            headers: getDefaultHeaders(),
        }),

    /** The Saved page has been seen: everything saved so far stops being new for this user. */
    markSavedViewed: async (): Promise<void> => {
        await fetchWithAuth<void>(DA_ENDPOINTS.SAVED_VIEWED, {
            method: 'POST',
            headers: getDefaultHeaders(),
        });
    },
};

// Data Extraction API functions
export const dataExtractionApi = {
    // Get the current status of the extraction process
    getStatus: async (council: CouncilKey): Promise<ProcessingStatus> => {
        try {
            // Use the appropriate controller based on council
            const endpoint = council === 'gold-coast'
                ? `${SEARCH_ENDPOINTS.GOLD_COAST_SCRAPING}/status`
                : `${SEARCH_ENDPOINTS.SOUTH_AUSTRALIA_SCRAPING}/status`;

            const response = await fetchWithAuth<ProcessingStatus>(endpoint, {
                method: 'GET',
                headers: getDefaultHeaders(),
            });

            return response;
        } catch (error) {
            // If the server endpoint fails, return a default completed status
            // to prevent UI from showing an extraction in progress
            return {
                id: 'error-id',
                providerCode: council,
                lastProcessedPropertyId: '',
                processedCount: 0,
                enrichedCount: 0,
                pdfExtractedCount: 0,
                currentPage: 0,
                isCompleted: true,
                errorMessage: error instanceof Error ? error.message : 'Unknown error',
                startTime: new Date().toISOString(),
                lastUpdated: new Date().toISOString()
            };
        }
    },

    // Start the extraction process
    startExtraction: async (
        council: CouncilKey,
        params: {
            maxPages?: number;
            startPage?: number;
            sortOldestFirst?: boolean;
            useFileSource?: boolean;
            enrichData?: boolean;
        } = {}
    ): Promise<void> => {
        const { maxPages, startPage, sortOldestFirst, useFileSource, enrichData } = params;

        // Use the appropriate controller based on council
        const baseEndpoint = council === 'gold-coast'
            ? SEARCH_ENDPOINTS.GOLD_COAST_SCRAPING
            : SEARCH_ENDPOINTS.SOUTH_AUSTRALIA_SCRAPING;

        // Both controllers use the same endpoint pattern
        const endpoint = `${baseEndpoint}/scrape-applications`;

        // Build query parameters
        const queryParams = new URLSearchParams();
        // Always include maxPagesToFetch and startPage parameters, even if they're the default values
        queryParams.append('maxPagesToFetch', maxPages !== undefined ? maxPages.toString() : '10000');
        queryParams.append('startPage', startPage !== undefined ? startPage.toString() : '1');
        if (sortOldestFirst !== undefined) queryParams.append('sortOldestFirst', sortOldestFirst.toString());
        if (useFileSource !== undefined) queryParams.append('useFileSource', useFileSource.toString());
        if (enrichData !== undefined) queryParams.append('enrichData', enrichData.toString());

        const queryString = queryParams.toString();
        const fullEndpoint = queryString ? `${endpoint}?${queryString}` : endpoint;

        await fetchWithAuth<void>(fullEndpoint, {
            method: 'POST',
            headers: getDefaultHeaders(),
        });

        // After successful start, get the initial status
        await dataExtractionApi.getStatus(council);
    },

    // Start the enrichment process
    startEnrichment: async (council: CouncilKey): Promise<void> => {
        // Use the appropriate controller based on council
        const baseEndpoint = council === 'gold-coast'
            ? SEARCH_ENDPOINTS.GOLD_COAST_SCRAPING
            : SEARCH_ENDPOINTS.SOUTH_AUSTRALIA_SCRAPING;

        const endpoint = `${baseEndpoint}/enrich-applications`;

        await fetchWithAuth<void>(endpoint, {
            method: 'POST',
            headers: getDefaultHeaders(),
        });

        // After successful enrichment, update the status
        await dataExtractionApi.getStatus(council);
    },

    // Cancel the extraction process
    cancelExtraction: async (council: CouncilKey): Promise<void> => {
        // Use the appropriate controller based on council
        const baseEndpoint = council === 'gold-coast'
            ? SEARCH_ENDPOINTS.GOLD_COAST_SCRAPING
            : SEARCH_ENDPOINTS.SOUTH_AUSTRALIA_SCRAPING;

        const endpoint = `${baseEndpoint}/cancel`;

        await fetchWithAuth<void>(endpoint, {
            method: 'POST',
            headers: getDefaultHeaders(),
        });

        // After successful cancellation, update the status
        await dataExtractionApi.getStatus(council);
    },
};

// Helper function to get profile image URL
export function getProfileImageUrl(userId: string): string {
    // In production, construct URL directly to avoid double /api paths
    if (import.meta.env.PROD) {
        const origin = getApiOrigin();
        // Add a cache-busting parameter
        const timestamp = new Date().getTime();
        // Return the URL with only one query parameter and single /api path
        return `${origin}/api/ProfileImage/${userId}?v=${timestamp}`;
    }

    // For development, use createEndpoint for consistent URL handling
    const baseUrl = createEndpoint('ProfileImage');
    // Add a cache-busting parameter
    const timestamp = new Date().getTime();
    // Return the URL with only one query parameter
    return `${baseUrl}/${userId}?v=${timestamp}`;
}

// Auth API functions
export const authApi = {
    /**
     * Confirm a user's email address
     * @param userId The ID of the user
     * @param token The confirmation token
     */
    confirmEmail: async (userId: string, token: string): Promise<{ message: string }> => {
        const response = await fetchWithAuth<{ message: string }>(
            `${AUTH_ENDPOINTS.CONFIRM_EMAIL}?userId=${encodeURIComponent(userId)}&token=${encodeURIComponent(token)}`,
            {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
                credentials: 'include',
            }
        );
        return response;
    },
};

export const notificationApi = {
    // Get all notifications for the current user
    getNotifications: async (): Promise<Notification[]> => {
        return fetchWithAuth<Notification[]>(NOTIFICATION_ENDPOINTS.NOTIFICATIONS, {
            method: 'GET',
            headers: getDefaultHeaders()
        });
    },

    // Mark a notification as read
    markNotificationAsRead: async (notificationId: string): Promise<void> => {
        const url = NOTIFICATION_ENDPOINTS.NOTIFICATION_READ(notificationId);
        const response = await fetch(url, {
            method: 'PUT',
            headers: getDefaultHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            // Try to read text for diagnostics only; do not throw JSON parse errors
            const text = await response.text().catch(() => '');
            throw new Error(`Failed to mark notification as read. Status: ${response.status}. ${text}`);
        }
        // 204 No Content is expected; nothing else to do
    },

    // Mark all notifications as read
    markAllNotificationsAsRead: async (): Promise<void> => {
        const url = NOTIFICATION_ENDPOINTS.MARK_ALL_READ;
        const response = await fetch(url, {
            method: 'PUT',
            headers: getDefaultHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            const text = await response.text().catch(() => '');
            throw new Error(`Failed to mark all notifications as read. Status: ${response.status}. ${text}`);
        }
    },

    // Get all notification preferences for the current user
    getNotificationPreferences: async (): Promise<NotificationPreference[]> => {
        return fetchWithAuth<NotificationPreference[]>(NOTIFICATION_ENDPOINTS.NOTIFICATION_PREFERENCES, {
            method: 'GET',
            headers: getDefaultHeaders()
        });
    },

    // Update the LastCheckedAt timestamp for a notification preference (returns 204 No Content)
    updateNotificationPreferenceLastChecked: async (preferenceId: string): Promise<void> => {
        const url = NOTIFICATION_ENDPOINTS.NOTIFICATION_PREFERENCE_CHECK(preferenceId);
        const response = await fetch(url, {
            method: 'PUT',
            headers: getDefaultHeaders(),
            credentials: 'include',
        });
        if (!response.ok) {
            const text = await response.text().catch(() => '');
            throw new Error(`Failed to update LastCheckedAt. Status: ${response.status}. ${text}`);
        }
        // 204 is success; nothing to return
    },

    // Create a new notification preference
    createNotificationPreference: async (data: Partial<NotificationPreference>): Promise<NotificationPreference> => {
        return fetchWithAuth<NotificationPreference>(NOTIFICATION_ENDPOINTS.NOTIFICATION_PREFERENCES, {
            method: 'POST',
            headers: getDefaultHeaders(),
            body: JSON.stringify(data)
        });
    },

    // Update an existing notification preference
    updateNotificationPreference: async (id: string, data: Partial<NotificationPreference>): Promise<NotificationPreference> => {
        return fetchWithAuth<NotificationPreference>(`${NOTIFICATION_ENDPOINTS.NOTIFICATION_PREFERENCES}/${id}`, {
            method: 'PUT',
            headers: getDefaultHeaders(),
            body: JSON.stringify(data)
        });
    },

    // Delete a notification preference
    deleteNotificationPreference: async (id: string): Promise<{success: boolean, status?: number}> => {
        try {
            // Use fetchWithAuth for consistent URL handling
            const url = `${NOTIFICATION_ENDPOINTS.NOTIFICATION_PREFERENCES}/${id}`;

            const response = await fetchWithAuth<Response>(url, {
                method: 'DELETE',
                headers: getDefaultHeaders(),
                credentials: 'include'
            });

            // Return success status based on response
            return {
                success: response.ok,
                status: response.status
            };
        } catch {
            // Return failure with any available status code
            return {
                success: false,
                status: undefined
            };
        }
    },
};

export const impersonationApi = {
  start: async (userId: string, readOnly: boolean = true): Promise<void> => {
    await fetchWithAuth<void>(`/api/admin/impersonate/${encodeURIComponent(userId)}?readOnly=${readOnly}`, {
      method: 'POST',
      headers: getDefaultHeaders(),
    });
  },
  setMode: async (readOnly: boolean): Promise<void> => {
    await fetchWithAuth<void>(`/api/admin/impersonate/mode?readOnly=${readOnly}`, {
      method: 'POST',
      headers: getDefaultHeaders(),
    });
  },
  stop: async (): Promise<void> => {
    await fetchWithAuth<void>('/api/admin/impersonate/stop', {
      method: 'POST',
      headers: getDefaultHeaders(),
    });
  },
};

/** The plan a business is on, as the admin list reports it and the sandbox plan switch sets it. */
export type AdminPlan = 'intelligence' | 'marketplace' | 'none';

/** A sandbox business after its plan was switched — the list-row fields the switch changes. */
export interface SetSandboxPlanResult {
  id: string;
  plan: AdminPlan;
  subscriptionStatus: string | null;
  planName: string | null;
  subscriptionStartDate: string | null;
  cancelAtPeriodEnd: boolean;
  cancelAt: string | null;
  isActive: boolean;
}

export interface ResetTrialResult {
  id: string;
  subscriptionStatus: string;
  subscriptionStartDate: string;
  subscriptionEndDate: string;
  planName: string | null;
  councilAreas: string[];
}

/** What a sandbox demo-data reset created. `marketplaceDays` is one row per UTC day of stats. */
export interface ResetDemoDataResult {
  deals: number;
  enquiries: number;
  marketplaceDays: number;
}

/** A user a sandbox deletion touches. `reason` says why a KEPT user is being kept. */
export interface SandboxDeletionUser {
  userId: string;
  email: string;
  name: string;
  reason: string | null;
}

/** One kind of record keyed to the business, and how many of it there are. */
export interface SandboxRecordCount {
  label: string;
  count: number;
}

/**
 * What deleting a sandbox WOULD do, computed by the server without changing anything.
 * `confirmationText` is exactly what must be typed back to delete — the name, or the id when the
 * business has none. `keptRecords` reference the business but are deliberately not deleted.
 */
export interface SandboxDeletionPreview {
  businessId: string;
  businessName: string;
  confirmationText: string;
  usersToDelete: SandboxDeletionUser[];
  usersToKeep: SandboxDeletionUser[];
  records: SandboxRecordCount[];
  keptRecords: SandboxRecordCount[];
  hasStripeSubscription: boolean;
}

/** What a sandbox deletion did. `stripeSubscriptionCancelled` is null when there was nothing to cancel. */
export interface SandboxDeletionResult {
  businessId: string;
  businessName: string;
  deletedUsers: SandboxDeletionUser[];
  keptUsers: SandboxDeletionUser[];
  deletedRecords: SandboxRecordCount[];
  stripeSubscriptionCancelled: boolean | null;
}

export const adminSubscriptionsApi = {
  // Resets a business's 14-day free trial (start = now, end = +14d, status = "trialing"). Admin-only;
  // the server rejects accounts on a live paid subscription.
  resetTrial: async (businessId: string): Promise<ResetTrialResult> => {
    return fetchWithAuth<ResetTrialResult>(`/api/admin/subscriptions/${encodeURIComponent(businessId)}/reset-trial`, {
      method: 'POST',
      headers: getDefaultHeaders(),
    });
  },
  // Replaces a SANDBOX business's deals, enquiries and marketplace stats with fresh demo data.
  // Admin-only; the server refuses (400) for any business that is not a sandbox.
  resetDemoData: async (businessId: string): Promise<ResetDemoDataResult> => {
    return fetchWithAuth<ResetDemoDataResult>(`/api/admin/subscriptions/${encodeURIComponent(businessId)}/demo-data`, {
      method: 'POST',
      headers: getDefaultHeaders(),
    });
  },
  // Puts a SANDBOX business straight onto a plan, with no Stripe checkout, and detaches it from its
  // sandbox Stripe subscription so the background sync cannot revert it. Admin-only; the server
  // refuses (400) any business that is not a sandbox.
  setSandboxPlan: async (businessId: string, plan: AdminPlan): Promise<SetSandboxPlanResult> => {
    return fetchWithAuth<SetSandboxPlanResult>(`/api/admin/subscriptions/${encodeURIComponent(businessId)}/sandbox-plan`, {
      method: 'PUT',
      headers: getDefaultHeaders(),
      body: JSON.stringify({ plan }),
    });
  },
  // What deleting a SANDBOX business would delete and keep. Read-only on the server. Admin-only;
  // 400 for any business that is not a sandbox.
  getSandboxDeletionPreview: async (businessId: string): Promise<SandboxDeletionPreview> => {
    return fetchWithAuth<SandboxDeletionPreview>(`/api/admin/subscriptions/${encodeURIComponent(businessId)}/sandbox-deletion-preview`, {
      method: 'GET',
      headers: getDefaultHeaders(),
    });
  },
  // PERMANENTLY deletes a SANDBOX business, its records, and the users nothing else needs.
  // `confirmName` must be the preview's confirmationText typed back — the server refuses (400)
  // anything else, and any business that is not a sandbox.
  deleteSandbox: async (businessId: string, confirmName: string): Promise<SandboxDeletionResult> => {
    return fetchWithAuth<SandboxDeletionResult>(`/api/admin/subscriptions/${encodeURIComponent(businessId)}/sandbox`, {
      method: 'DELETE',
      headers: getDefaultHeaders(),
      body: JSON.stringify({ confirmName }),
    });
  },
};
